// Boot screen (§14.2): the logo stamp over the live diorama, a procedural progress bar (fonts,
// shaders/diorama, audio pre-render after unlock) and "Click / tap / press any button to start".
// The first gesture (click, tap, key or any gamepad button) unlocks audio and continues.
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { icon } from '../icons';
import { el, setStyle, setText, toggleClass } from '../ui';
import { logo } from '../widgets';

const ARM_DELAY_MS = 250;

export function create(deps: UiDeps): LiveScreen {
  let armed = false;
  let armTimer = 0;
  let shownAt = 0;
  const go = (): void => {
    if (!armed) return;
    armed = false;
    window.removeEventListener('keydown', onKey);
    deps.services.playUi('click');
    deps.actions.start();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    go();
  };
  const fill = el('i');
  const status = el('span', { class: 'boot-status', text: 'Warming up the beans…' });
  const steps = {
    fonts: el('span', { class: 'boot-step' }, icon('check'), 'Fonts'),
    world: el('span', { class: 'boot-step' }, icon('check'), 'Diorama'),
    audio: el('span', { class: 'boot-step' }, icon('check'), 'Sound'),
  };
  const prompt = el('div', { class: 'boot-prompt' },
    el('span', { class: 'bp-text', text: 'Click / tap / press any button to start' }));
  // `click`, not `pointerdown`: the mousedown's default focus change would blur the next screen's field.
  const root = el('div', { class: 'screen boot', attrs: { role: 'button', 'aria-label': 'Start', tabindex: 0, 'data-noback': true }, on: { click: go } },
    el('div', { class: 'sunburst' }),
    el('div', { class: 'boot-center' },
      logo('THE BEAN-BONKING PARTY BRAWLER'),
      el('div', { class: 'boot-load' },
        el('div', { class: 'bar boot-bar' }, fill),
        el('div', { class: 'boot-steps' }, steps.fonts, steps.world, steps.audio),
        status),
      prompt),
    el('div', { class: 'boot-foot', text: '1–8 players • bots fill the arena • best with friends' }));
  return {
    el: root,
    onShow() {
      shownAt = performance.now();
      armed = false;
      clearTimeout(armTimer);
      armTimer = window.setTimeout(() => {
        armed = true;
      }, ARM_DELAY_MS);
      window.addEventListener('keydown', onKey);
    },
    onHide() {
      armed = false;
      clearTimeout(armTimer);
      window.removeEventListener('keydown', onKey);
    },
    // Any gamepad button (or a nav key while the screen has focus) starts too.
    onNav() {
      go();
      return true;
    },
    update() {
      const l = deps.services.loading();
      const elapsed = performance.now() - shownAt;
      // Audio pre-renders after the unlock gesture, so it never blocks the bar's "ready" state.
      const k = (l.fonts ? 0.4 : Math.min(0.35, elapsed / 3000)) + (l.world ? 0.45 : Math.min(0.4, elapsed / 6000)) + (l.audio ? 0.15 : 0);
      setStyle(fill, 'width', `${Math.round(Math.min(1, l.fonts && l.world ? Math.max(k, 0.85) : k) * 100)}%`);
      toggleClass(steps.fonts, 'done', l.fonts);
      toggleClass(steps.world, 'done', l.world);
      toggleClass(steps.audio, 'done', l.audio);
      const ready = l.fonts && l.world;
      toggleClass(root, 'ready', ready);
      setText(status, ready ? 'Ready to bonk!' : !l.fonts ? 'Loading fonts…' : 'Building the diorama…');
    },
  };
}
